<template>
  <div class="w-full max-h-screen bg-background flex flex-col relative">
    <page-header title="Job Sheets" backTo="service">
      <router-link
          :to="{ name: 'engineer-job-sheet-create' }"
          class="flex items-center bg-green-200 border hover:bg-green-300 border-green-400 hover:border-green-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md px-2 py-1"
      >
        <svg
            class="stroke-current h-5 w-5 mr-2"
            fill="none"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            viewBox="0 0 24 24"
        >
          <path d="M12 5v14M5 12h14"/>
        </svg>
        <span>Create Job Sheet</span>
      </router-link>
    </page-header>

    <div class="p-6 bg-background flex-1 overflow-y-scroll">
      <div class="w-full bg-white p-5 rounded shadow-md relative">
        <!-- Toolbar with Tab Headers and Search Controls -->
        <div class="w-full flex items-center justify-between">
          <div class="flex -mx-0 mr-2">
            <TabHeaders :tabs="tabs" :selectedTab="currentTab" @tab-selected="onTabSelected"/>
          </div>
          <div class="flex flex-col flex-1">
            <div class="flex justify-end">
              <div class="flex items-center mr-4">
                <label class="mr-2">From Date:</label>
                <Calendar
                    class="w-full lg:w-auto"
                    v-model="fromDate"
                    dateFormat="dd/mm/yy"
                    :showIcon="true"
                />
              </div>
              <div class="flex items-center ">
                <label class="mr-2">To Date:</label>
                <Calendar
                    class="w-full lg:w-auto"
                    v-model="toDate"
                    dateFormat="dd/mm/yy"
                    :showIcon="true"
                />
              </div>
              <input
                  type="text"
                  class="ml-10 w-full lg:w-1/4 rounded-l bge-input bge-input-spacing"
                  placeholder="Search "
                  v-model="searchInput"
                  @keydown.enter="triggerSearch"
              />
              <button
                  @click="triggerSearch"
                  class="rounded-r bg-blue-200 hover:bg-blue-300 border border-blue-400 hover:border-blue-500 p-2 hover:shadow active:shadow-inner transition-colors duration-300 ease-in-out"
              >
                <Icon iconType="search"/>
              </button>
            </div>
            <div class="flex justify-end mt-5">
              <div class="pr-6">
                <Toggle
                    label="Pending"
                    :labelPosition="'left'"
                    :checked="isPending"
                    @value-changed="(value) => (isPending = value)"

                />
              </div>
              <div class>
                <Toggle
                    label="Completed"
                    :labelPosition="'left'"
                    :checked="isCompleted"
                    @value-changed="(value) => (isCompleted = value)"
                />
              </div>
            </div>
          </div>
        </div>


        <!-- Tab Content -->

        <TabContent :currentTab="currentTab">

          <!-- Default slot for other tabs -->
          <template v-slot:[currentTab]>
            <DataTableComponent :columns="columns" @row-click="onRowClick" key="schedule">
              <!--              <template v-slot:timestamp-slot="{ rowData }">-->
              <!--                {{ formatTimestamp(rowData.warranty_expiry) }}-->
              <!--              </template>-->

              <template v-slot:customer-postcode-slot="{ rowData }">
                <template v-if="rowData.customer && rowData.customer.default_address">
                  {{ getDefaultAddressPostcode(rowData.customer.default_address) }}
                </template>
                <template v-else>
                </template>
              </template>
              <template v-slot:engineer-name-slot="{ rowData }">
                          <span v-if="rowData && rowData.engineer">
                          {{
                              rowData.engineer.first_name
                            }} {{ rowData.engineer.last_name }}
                         </span>
              </template>

              <template v-slot:job-completed-slot="{ rowData }">
                <template v-if="rowData.completed_at">
                  {{ formatTimestamp(rowData.completed_at) }}
                </template>
                <template v-else>
                  PENDING
                </template>
              </template>

            </DataTableComponent>
          </template>
        </TabContent>


      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from "@/components/PageHeader.vue";
import Spinner from "@/components/Spinner.vue";
import Panel from "@/components/Panel.vue";
import TabHeaders from "@/components/TabHeaders.vue";
import TabContent from "@/components/TabContent.vue";
import DataTableComponent from "@/views/HR/Engineer/JobSheet/Components/DataTableComponent.vue";
import {mapActions, mapState} from "vuex";
import Icon from "@/components/Icon.vue";
import notificationMixin from "@/mixins/notificationMixin";
import StaffAutocomplete from "@/components/AutoComplete/StaffAutoComplete.vue";

const Toggle = () => import("@/components/Toggle.vue");
const IsAuthorized = () => import("@/components/Auth/IsAuthorized.vue");

export default {
  mixins: [notificationMixin],
  components: {
    StaffAutocomplete,
    PageHeader,
    Spinner,
    Panel,
    IsAuthorized,
    TabHeaders,
    TabContent,
    DataTableComponent,
    Icon,
    Toggle
  },
  data() {
    return {
      tabs: [
        {
          id: "external",
          label: "External",
        },
        {id: "internal", label: "Internal",},
      ],
      columns: [
        {
          field: "customer.name",
          header: "Customer",
          sortable: true,
          custom: false,
          width: '15%',
        },
        {
          field: "customer.default_address.postcode",
          header: "Postcode",
          sortable: true,
          custom: true,
          slotName: "customer-postcode-slot",
          width: '8%',
        },
        {
          field: "equipment.product_code",
          header: "Code",
          sortable: true,
          custom: false,
          width: '8%',
        },
        {
          field: "equipment.name",
          header: "Description",
          sortable: false,
          custom: false,
          width: '15%',
        },
        {
          field: "equipment.serial_number",
          header: "Serial Number",
          sortable: true,
          custom: false,
          width: '12%',
        },
        {
          field: "completed_at",
          header: "Job Completed",
          sortable: true,
          custom: true,
          slotName: "job-completed-slot",
          width: '12%',
        },
        {
          field: "engineer.first_name",
          header: "Engineer Name",
          sortable: true,
          custom: true,
          slotName: "engineer-name-slot",
          width: '12%',
        },
        {
          field: "job_sheet_no",
          header: "Job Sheet No",
          sortable: true,
          custom: false,
          slotName: "scheduled-slot",
          width: '12%',
        },
        {
          field: "action",
          header: "",
          sortable: false,
          custom: true,
          slotName: "action-slot",
          width: '12%',
        },
      ],
    };
  },
  computed: {
    ...mapState("jobSheet", ["currentTab", "searchText",]),

    ...mapState("jobSheet", {
      currentTabState: (state) => state.tabs[state.currentTab],
    }),

    searchInput: {
      get() {
        return this.currentTabState.searchText;
      },
      set(value) {
        this.$store.commit("jobSheet/UPDATE_SEARCH_TEXT", {tab: this.currentTab, searchText: value});
      },
    },

    fromDate: {
      get() {

        const fromDate = this.currentTabState.fromDate;
        return fromDate ? new Date(fromDate) : null;
      },
      set(value) {

        this.$store.commit("jobSheet/UPDATE_FROM_DATE", {tab: this.currentTab, fromDate: value});
      }
    },

    toDate: {
      get() {
        const toDate = this.currentTabState.toDate;
        return toDate ? new Date(toDate) : null;
      },
      set(value) {
        this.$store.commit("jobSheet/UPDATE_TO_DATE", {tab: this.currentTab, toDate: value});
      }
    },

    isPending: {
      get() {
        return this.currentTabState.is_pending;
      },
      set(value) {
        this.$store.commit("jobSheet/UPDATE_IS_PENDING", {tab: this.currentTab, isPending: value});
      }
    },

    isCompleted: {
      get() {
        return this.currentTabState.is_completed;
      },
      set(value) {
        this.$store.commit("jobSheet/UPDATE_IS_COMPLETED", {tab: this.currentTab, isCompleted: value});
      }
    },
  },
  mounted() {
    this.fetchTableData();
  },
  methods: {
    ...mapActions("jobSheet", [
      "fetchTableData",
      "updateSearchCriteria",
      "updateTab",
    ]),


    getDefaultAddressPostcode(default_address) {

      return default_address ? default_address.postcode : 'N/A';
    },
    getDefaultPeoplePhone(peopleArray) {

      const defaultPhone = peopleArray.find(people => people.is_default);
      return defaultPhone ? defaultPhone.mobile || defaultPhone.telephone : 'N/A';
    },
    onRowClick(event) {

      this.Edit(event.data);
    },
    formatTimestamp(unixTimestamp) {
      return this.$moment.unix(unixTimestamp).format('DD-MM-YYYY');
    },
    onTabSelected(newTab) {
      this.setTab(newTab);
    },
    setTab(newTab) {
      this.$store.dispatch("jobSheet/updateTab", newTab);
    },
    triggerSearch() {
      this.setTab(this.currentTab);
    },

    Edit(rowObj) {
      this.$router.push({
        name: "engineer-job-sheet-edit",
        params: {
          job_sheet_id: rowObj.job_sheet_id
        },
      });
    },


  },
};
</script>